import { useState, useEffect, useContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Main from "./components/Main";
import SearchResults from "./components/SearchResults";
import MasjidDetails from "./components/MasjidDetails";
import NotFound from "./components/NotFound";
import NearbyPage from "./components/NearbyPage";
import FavouritesPage from "./components/FavouritesPage";
import InitialRedirect from "./components/InitialRedirect";
import LanguageSelector from "./components/LanguageSelector";
import { MasajidProvider, MasajidContext } from "./contexts/MasajidContext";
import { analytics } from "./services/firebase";
import { logEvent } from "firebase/analytics";

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 1024px)");

    const handler = (e) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    mql.addEventListener("change", handler);

    return () => mql.removeEventListener("change", handler);
  }, []);

  return isMobile;
};

function AppContent({ children }) {
  const { isLanguageSet } = useContext(MasajidContext);

  if (!isLanguageSet) {
    return <LanguageSelector />;
  }

  return children;
}

function App() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [isIos, setIsIos] = useState(false);

  // mobile/tablet detection
  const isMobile = useIsMobile();

  // iOS detection function
  const isIOSDevice = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  };

  // Check if running in standalone mode (PWA installed)
  const isStandaloneMode = () => {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    );
  };

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Check if app is already installed (PWA standalone mode)
    if (isStandaloneMode()) {
      setIsAppInstalled(true);
    }

    // Detect iOS devices for manual install prompt
    if (isIOSDevice()) {
      setIsIos(true);
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
    };
  }, []);

  useEffect(() => {
    logEvent(analytics, "app_open");
  }, []);

  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === "accepted") {
          console.log("User accepted the install prompt");
          logEvent(analytics, "pwa_install_accepted");
        } else {
          console.log("User dismissed the install prompt");
        }
        setDeferredPrompt(null);
      });
    }
  };

  return (
    <div className="App">
      <MasajidProvider>
        <AppContent>
          <Router>
            <InitialRedirect>
              <div>
                <Routes>
                  <Route path="/" element={<Main />} />
                  <Route path="/search-results" element={<SearchResults />} />
                  <Route path="/masjid/:username" element={<MasjidDetails />} />
                  <Route path="/nearby" element={<NearbyPage />} />
                  <Route path="/favourites" element={<FavouritesPage />} />
                  <Route path="/not-found" element={<NotFound />} />
                  <Route
                    path="*"
                    element={<Navigate to="/not-found" replace />}
                  />
                </Routes>
              </div>
            </InitialRedirect>
          </Router>
        </AppContent>
      </MasajidProvider>

      {/* PWA install prompts */}
      {!isAppInstalled && isMobile && (
        <>
          {/* Standard PWA install prompt for Android/Chrome */}
          {deferredPrompt && (
            <div className="install-button-container">
              <button onClick={handleInstallClick} className="install-button">
                Install App
              </button>
            </div>
          )}

          {/* iOS manual install instructions */}
          {isIos && !deferredPrompt && (
            <div className="ios-install-guide">
              <p>
                To install this app, open this page in <strong>Safari</strong>,
                then:
              </p>
              <ol>
                <li>
                  Tap the Share button{" ( "}
                  <img
                    src="/assets/img/share_icon.png"
                    alt="Safari share icon"
                    style={{ width: "12px", verticalAlign: "middle" }}
                  />
                  {" ) "}
                  at the bottom of your browser
                </li>
                <li>
                  Scroll down and tap <strong>Add to Home Screen</strong>
                </li>
                <li>
                  Turn on <strong>Open as Web App</strong>
                </li>
                <li>
                  Tap <strong>Add</strong>
                </li>
              </ol>
              <p style={{ fontSize: "0.85em", marginTop: "10px" }}>
                Need help?{" "}
                <a
                  id="website-link"
                  href="https://support.apple.com/en-gb/guide/iphone/iphea86e5236/ios"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Apple's instructions
                </a>
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default App;
